import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { Link } from 'react-router';
import { Logo } from './Logo';

/** White top bar with the logo, as in the Figma product frame (1:75). */
export function SiteHeader({ children }: { children?: ReactNode }) {
  return (
    <header className="border-b border-divider bg-card">
      <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
        <Logo />
        {children && <div className="flex items-center gap-2">{children}</div>}
      </div>
    </header>
  );
}

/** Centered card used by every sign-in page. */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />
      <main className="flex flex-1 items-start justify-center px-4 py-10 sm:px-6 sm:py-14">
        <div className="w-full max-w-md rounded-2xl border border-line bg-card p-6 shadow-[0_1px_2px_rgba(27,21,47,0.04)] sm:p-8">
          <h1 className="text-2xl font-bold text-ink">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-muted">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </main>
      {footer && <p className="pb-6 text-center text-sm text-muted">{footer}</p>}
      <p className="pb-8 text-center text-xs text-muted">
        <Link to="/privacy" className="hover:text-ink hover:underline">
          Privacy
        </Link>
        {' · '}
        <Link to="/terms" className="hover:text-ink hover:underline">
          Terms
        </Link>
      </p>
    </div>
  );
}

export const inputClass =
  'block w-full rounded-xl border border-line bg-field px-3.5 py-2.5 text-ink placeholder:text-subtle focus:border-ink focus:bg-card focus:outline-none focus:ring-2 focus:ring-ink/10';

export function Field({
  label,
  hint,
  ...input
}: { label: string; hint?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      <input
        id={id}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={inputClass}
        {...input}
      />
      {hint && (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

const BUTTON_STYLES = {
  /** Ink pill: the main action ("Create post", "Accept challenge"). */
  primary: 'bg-ink text-white hover:bg-ink-soft',
  /** Yellow pill: playful highlights ("Play"). */
  brand: 'bg-brand text-ink hover:bg-brand-hover',
  /** White pill with a border: secondary actions ("Share"). */
  secondary: 'border border-line bg-card text-ink hover:border-muted',
  danger: 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
} as const;

export function buttonClass(variant: keyof typeof BUTTON_STYLES = 'primary', block = true) {
  return `inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${block ? 'w-full' : ''} ${BUTTON_STYLES[variant]}`;
}

export function Button({
  busy,
  variant = 'primary',
  children,
  ...button
}: {
  busy?: boolean;
  variant?: keyof typeof BUTTON_STYLES;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="submit"
      disabled={busy || button.disabled}
      aria-busy={busy || undefined}
      className={buttonClass(variant)}
      {...button}
    >
      {busy ? 'Please wait…' : children}
    </button>
  );
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'success' | 'info';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-red-200 bg-red-50 text-red-800',
    success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    info: 'border-line bg-field text-ink',
  }[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`rounded-xl border px-4 py-3 text-sm ${styles}`}
    >
      {children}
    </div>
  );
}

/** Agreement line under sign-up and sign-in (social sign-in can create an account too). */
export function LegalNotice({ action }: { action: string }) {
  return (
    <p className="mt-4 text-center text-xs leading-relaxed text-muted">
      By {action}, you agree to MOGZO’s{' '}
      <Link
        to="/terms"
        className="font-semibold text-ink underline decoration-brand underline-offset-2"
      >
        Terms of Service
      </Link>{' '}
      and{' '}
      <Link
        to="/privacy"
        className="font-semibold text-ink underline decoration-brand underline-offset-2"
      >
        Privacy Policy
      </Link>
      .
    </p>
  );
}

/** Ink link with a yellow underline: on-brand and readable (yellow text alone fails contrast). */
export function TextLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="font-semibold text-ink underline decoration-brand decoration-2 underline-offset-4 hover:decoration-ink"
    >
      {children}
    </Link>
  );
}

export function Divider({ label }: { label: string }) {
  return (
    <div className="my-6 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-muted">
      <span className="h-px flex-1 bg-line" />
      {label}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

export function PageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center text-sm text-muted" role="status">
      Loading…
    </div>
  );
}
